import { Component, OnInit } from '@angular/core';
import { CoreService, ReceiptReportResult, ReceiptReportTableInfo, ReceiptSqlCritique } from 'src/app/core/helpers/core.service';

@Component({
  selector: 'app-receipt-insights',
  templateUrl: './receipt-insights.component.html',
  styleUrls: ['./receipt-insights.component.scss']
})
export class ReceiptInsightsComponent implements OnInit {

  question = '';
  isLoading = false;
  errorMessage = '';
  result: ReceiptReportResult | null = null;
  showDetails = false;

  // "Investigate this result" — self-service trust check on the just-generated SQL
  isInvestigating = false;
  investigateError = '';
  critique: ReceiptSqlCritique | null = null;
  showCritiqueDiagnostics = false;

  readonly exampleQuestions = [
    'How many receipts were approved in the last 30 days?',
    'What are the top 10 vendors by receipt count?',
    'Show total receipt value per day for the last 30 days',
    'What percentage of receipts are flagged as AI-generated this quarter?',
    'What are the top 10 most purchased items by quantity?'
  ];

  // Table scope settings
  showSettings = false;
  isLoadingTables = false;
  isSavingTables = false;
  tables: ReceiptReportTableInfo[] = [];

  // chart.js (ng2-charts v1 style — options untyped, matches this repo's ReportsComponent)
  chartType: 'line' | 'bar' = 'bar';
  chartLabels: string[] = [];
  chartDatasets: any[] = [{ data: [], label: '' }];
  chartTitle = '';
  chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    legend: { display: false },
    tooltips: {
      backgroundColor: 'rgba(0,0,0,0.8)',
      titleFontSize: 14,
      bodyFontSize: 13,
      cornerRadius: 6
    },
    scales: {
      yAxes: [{
        ticks: { beginAtZero: true, fontColor: '#666', fontSize: 12 },
        gridLines: { color: 'rgba(0, 0, 0, 0.05)', drawBorder: false }
      }],
      xAxes: [{
        ticks: { autoSkip: false, fontColor: '#666', fontSize: 11, maxRotation: 45, minRotation: 0 },
        gridLines: { display: false }
      }]
    },
    animation: { duration: 1000, easing: 'easeInOutQuart' }
  };

  constructor(private coreService: CoreService) { }

  ngOnInit() {
    this.loadTableConfig();
  }

  toggleSettings() {
    this.showSettings = !this.showSettings;
    if (this.showSettings && this.tables.length === 0) {
      this.loadTableConfig();
    }
  }

  loadTableConfig() {
    this.isLoadingTables = true;
    this.coreService.getReceiptReportTableConfig().subscribe({
      next: (res) => {
        this.tables = res.tables;
        this.isLoadingTables = false;
      },
      error: () => {
        this.isLoadingTables = false;
      }
    });
  }

  saveTableConfig() {
    const enabledTables = this.tables.filter(t => t.enabled).map(t => t.name);
    this.isSavingTables = true;
    this.coreService.updateReceiptReportTableConfig(enabledTables).subscribe({
      next: () => {
        this.isSavingTables = false;
      },
      error: () => {
        this.isSavingTables = false;
      }
    });
  }

  useExample(example: string) {
    this.question = example;
    this.ask();
  }

  ask() {
    const trimmed = this.question.trim();
    if (!trimmed) {
      return;
    }

    this.isLoading = true;
    this.errorMessage = '';
    this.result = null;
    this.critique = null;
    this.investigateError = '';
    this.showCritiqueDiagnostics = false;

    this.coreService.queryReceiptReport(trimmed).subscribe({
      next: (res) => {
        this.isLoading = false;
        this.result = res;
        this.buildChart(res);
      },
      error: (err) => {
        this.isLoading = false;
        const detail = err && err.error && (err.error.detail || err.error.Detail);
        this.errorMessage = detail || 'Something went wrong generating that report. Try rephrasing the question.';
      }
    });
  }

  investigate() {
    if (!this.result || !this.result.generatedSql) {
      return;
    }

    this.isInvestigating = true;
    this.investigateError = '';
    this.critique = null;

    this.coreService.investigateReceiptReport(this.question.trim(), this.result.generatedSql).subscribe({
      next: (res) => {
        this.isInvestigating = false;
        this.critique = res;
      },
      error: () => {
        this.isInvestigating = false;
        this.investigateError = 'Could not investigate this result right now. Try again in a moment.';
      }
    });
  }

  get critiqueVerdictClass(): string {
    if (!this.critique) {
      return '';
    }
    const verdict = this.critique.verdict.toLowerCase();
    if (verdict.includes('trustworthy')) {
      return 'verdict-trustworthy';
    }
    if (verdict.includes('likely wrong')) {
      return 'verdict-likely-wrong';
    }
    return 'verdict-questionable';
  }

  private buildChart(res: ReceiptReportResult) {
    if (!res.chart || !res.rows || res.rows.length === 0) {
      return;
    }
    const chart = res.chart;

    if (chart.chartType === 'line' || chart.chartType === 'bar') {
      this.chartType = chart.chartType;
      this.chartLabels = res.rows.map(r => this.toLabel(r[chart.xAxisColumn]));
      this.chartDatasets = [{
        data: res.rows.map(r => Number(r[chart.yAxisColumn]) || 0),
        label: chart.yAxisColumn,
        backgroundColor: 'rgba(102, 126, 234, 0.5)',
        borderColor: 'rgba(102, 126, 234, 1)',
        fill: chart.chartType === 'line'
      }];
      this.chartTitle = chart.title || '';
    }
  }

  private toLabel(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value);
  }

  get statValue(): any {
    if (!this.result || !this.result.chart || this.result.chart.chartType !== 'stat') {
      return null;
    }
    const col = this.result.chart.yAxisColumn;
    return this.result.rows[0] ? this.result.rows[0][col] : null;
  }
}
